import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppointmentSummaryGroupResponse, AppointmentSummaryResponse } from '../../api/types';
import { Columns, type Column } from '../../components/charts/Columns';
import { OutcomeBars, OutcomeLegend, type OutcomeRow } from '../../components/charts/OutcomeBars';
import { OUTCOME_COLOURS } from '../../components/charts/outcomes';
import { RankedBars } from '../../components/charts/RankedBars';
import { WeekHeatmap } from '../../components/charts/WeekHeatmap';
import { bucketDays, daysBetween, granularityFor, percent } from '../../components/charts/buckets';
import { bucketLabel, bucketTitle } from '../../components/charts/dates';
import { count } from '../../i18n/format';
import { useLocalisedName } from '../../i18n/localisedName';

/**
 * The appointments screen's charts (CR-33), drawn from the summary of the same search the list
 * below runs — same dates, same filters — so a chart and the rows under it never disagree.
 *
 * What it is for is noticing. The strip says how the range went; the time chart says when it went
 * wrong; status, service, branch and doctor say where. "Still open" counts appointments whose day
 * has passed without anyone marking them, which is the one number here that means somebody at the
 * desk has something to do.
 *
 * A bar is a way into the list: pressing a status, a service, a branch or a doctor sets that filter
 * on the screen, and pressing it again clears it. Only the filters the screen itself offers this
 * person can be set this way — the doctor bars do nothing for someone who cannot see the staff list.
 */
export function AppointmentsOverview({
  summary,
  from,
  to,
  today,
  selected,
  onFilter,
  showBranches,
}: {
  readonly summary: AppointmentSummaryResponse;
  readonly from: string;
  readonly to: string;
  /** The clinic's today, which decides what "already past" means. */
  readonly today: string;
  readonly selected: {
    readonly status: string;
    readonly serviceId: string;
    readonly branchId: string;
    readonly employmentId: string;
  };
  readonly onFilter: {
    readonly status: (status: string) => void;
    readonly serviceId?: (id: string) => void;
    readonly branchId?: (id: string) => void;
    readonly employmentId?: (id: string) => void;
  };
  readonly showBranches: boolean;
}) {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const [serviceView, setServiceView] = useState<'service' | 'type'>('service');

  const total = summary.total ?? 0;
  const completed = summary.completed ?? 0;
  const noShow = summary.noShow ?? 0;
  const open = summary.open ?? 0;
  const days = summary.byDay ?? [];
  const pastOpen = days
    .filter((day) => (day.date ?? '') < today)
    .reduce((running, day) => running + (day.open ?? 0), 0);
  const dayCount = Math.max(1, daysBetween(from, to));

  // Pressing the chosen bar again clears it, so the chart is also the way back out.
  const toggle = (current: string, apply?: (value: string) => void) =>
    apply ? (key: string) => apply(current === key ? '' : key) : undefined;
  const selectLabel = (current: string) => (row: { key: string; label: string }) =>
    current === row.key ? t('overview.showEverything') : t('overview.showOnly', { name: row.label });

  const groupRow = (group: AppointmentSummaryGroupResponse, fallback: string): OutcomeRow => ({
    key: group.id ?? '',
    label: group.nameEn || group.nameAr ? localised.name({ nameEn: group.nameEn, nameAr: group.nameAr }) : fallback,
    total: group.total ?? 0,
    completed: group.completed ?? 0,
    open: group.open ?? 0,
    noShow: group.noShow ?? 0,
    cancelled: group.cancelled ?? 0,
  });
  const services = (summary.byService ?? []).map((group) => groupRow(group, t('overview.noLongerListed')));
  const types = byCategory(summary.byService ?? [], (category) =>
    category ? t(`catalog.categories.${category}`) : t('overview.noType'),
  );
  const branches = (summary.byBranch ?? []).map((group) => groupRow(group, t('overview.noLongerListed')));
  const doctors = (summary.byDoctor ?? []).map((group) => groupRow(group, t('overview.formerDoctor')));

  const granularity = granularityFor(dayCount);
  const columns: Column[] = bucketDays(days, granularity).map((bucket) => ({
    key: bucket.start,
    label: bucketLabel(bucket, granularity, i18n.language),
    description: t('overview.columnDescription', {
      when: bucketTitle(bucket, granularity, i18n.language, (first, last) =>
        t('overview.weekOf', { from: first, to: last }),
      ),
      count: bucket.total,
      completed: bucket.completed,
      open: bucket.open,
      lost: bucket.noShow + bucket.cancelled,
    }),
    segments: [
      { value: bucket.completed, colour: OUTCOME_COLOURS.completed },
      { value: bucket.open, colour: OUTCOME_COLOURS.open },
      { value: bucket.noShow + bucket.cancelled, colour: OUTCOME_COLOURS.lost },
    ],
  }));

  const statuses = (summary.byStatus ?? [])
    .filter((row) => (row.count ?? 0) > 0 || row.status === selected.status)
    .map((row) => ({
      key: row.status ?? '',
      label: t(`board.status.${row.status ?? 'BOOKED'}`),
      value: row.count ?? 0,
      tone: row.status === 'NO_SHOW' || row.status === 'CANCELLED' ? ('alert' as const) : undefined,
    }));
  const channels = (summary.byChannel ?? [])
    .filter((row) => (row.count ?? 0) > 0)
    .map((row) => ({ key: row.channel ?? '', label: t(`appointment.channels.${row.channel ?? 'RECEPTION'}`), value: row.count ?? 0 }));

  // A breakdown with one row is not a comparison, so it is left out — unless that row is the
  // filter in force, where it is the way to clear it.
  const worthDrawing = (rows: readonly OutcomeRow[], current: string) => rows.length > 1 || current !== '';

  return (
    <div className="overview">
      <section className="measures" aria-label={t('overview.appointments.strip')}>
        <div className="measure measure--lead">
          <span className="measure__label">{t('overview.appointments.total')}</span>
          <span className="measure__value">{count(total)}</span>
          <span className="measure__note">
            {t('overview.appointments.perDay', { value: (total / dayCount).toFixed(1) })}
          </span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.completed')}</span>
          <span className="measure__value">{count(completed)}</span>
          <span className="measure__note">{t('overview.shareOfAll', { share: percent(completed, total) })}</span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.appointments.noShows')}</span>
          <span className="measure__value">{count(noShow)}</span>
          <span className="measure__note">
            {t('overview.appointments.noShowRate', { share: percent(noShow, completed + noShow) })}
          </span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.appointments.cancelled')}</span>
          <span className="measure__value">{count(summary.cancelled ?? 0)}</span>
          <span className="measure__note">
            {t('overview.appointments.late', { count: summary.lateCancellations ?? 0 })}
          </span>
        </div>
        <div className={pastOpen > 0 ? 'measure measure--alert' : 'measure'}>
          <span className="measure__label">{t('overview.open')}</span>
          <span className="measure__value">{count(open)}</span>
          <span className="measure__note">
            {pastOpen > 0
              ? t('overview.appointments.pastOpen', { count: pastOpen })
              : t('overview.appointments.noPastOpen')}
          </span>
        </div>
      </section>

      <section className="panel">
        <div className="panel__heading">
          <h2>{t('overview.appointments.overTime')}</h2>
          <p className="muted">{t(`overview.per.${granularity}`)}</p>
        </div>
        <Columns columns={columns} label={t('overview.appointments.overTime')} />
        <OutcomeLegend />
      </section>

      <div className="overview-grid">
        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.appointments.byStatus')}</h2>
            <p className="muted">{t('overview.pressToFilter')}</p>
          </div>
          <RankedBars
            rows={statuses}
            selected={selected.status}
            onSelect={toggle(selected.status, onFilter.status)}
            selectLabel={selectLabel(selected.status)}
          />
        </section>

        {services.length > 0 ? (
          <section className="panel">
            <div className="panel__header">
              <div className="panel__heading">
                <h2>{t('overview.appointments.byService')}</h2>
                {serviceView === 'service' && onFilter.serviceId ? (
                  <p className="muted">{t('overview.pressToFilter')}</p>
                ) : null}
              </div>
              <div className="tabs" role="tablist" aria-label={t('overview.appointments.byService')}>
                {(['service', 'type'] as const).map((view) => (
                  <button
                    key={view}
                    type="button"
                    role="tab"
                    aria-selected={serviceView === view}
                    className={serviceView === view ? 'tab tab--active' : 'tab'}
                    onClick={() => setServiceView(view)}
                  >
                    {t(`overview.appointments.view.${view}`)}
                  </button>
                ))}
              </div>
            </div>
            {serviceView === 'service' ? (
              <OutcomeBars
                rows={services}
                selected={selected.serviceId}
                onSelect={toggle(selected.serviceId, onFilter.serviceId)}
                selectLabel={selectLabel(selected.serviceId)}
              />
            ) : (
              <OutcomeBars rows={types} />
            )}
            <OutcomeLegend />
          </section>
        ) : null}

        {showBranches && worthDrawing(branches, selected.branchId) ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.appointments.byBranch')}</h2>
              {onFilter.branchId ? <p className="muted">{t('overview.pressToFilter')}</p> : null}
            </div>
            <OutcomeBars
              rows={branches}
              selected={selected.branchId}
              onSelect={toggle(selected.branchId, onFilter.branchId)}
              selectLabel={selectLabel(selected.branchId)}
            />
            <OutcomeLegend />
          </section>
        ) : null}

        {worthDrawing(doctors, selected.employmentId) ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.appointments.byDoctor')}</h2>
              {onFilter.employmentId ? <p className="muted">{t('overview.pressToFilter')}</p> : null}
            </div>
            <OutcomeBars
              rows={doctors}
              selected={selected.employmentId}
              onSelect={toggle(selected.employmentId, onFilter.employmentId)}
              selectLabel={selectLabel(selected.employmentId)}
            />
            <OutcomeLegend />
          </section>
        ) : null}

        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.appointments.busiest')}</h2>
            <p className="muted">{t('overview.appointments.busiestNote')}</p>
          </div>
          <WeekHeatmap hours={summary.byHour ?? []} label={t('overview.appointments.busiest')} />
        </section>

        {channels.length > 0 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.appointments.channels')}</h2>
              <p className="muted">{t('overview.appointments.channelsNote')}</p>
            </div>
            <RankedBars rows={channels} />
          </section>
        ) : null}
      </div>
    </div>
  );
}

/** The services folded into their catalogue categories, largest first ("service types"). */
function byCategory(
  groups: readonly AppointmentSummaryGroupResponse[],
  label: (category: string | undefined) => string,
): OutcomeRow[] {
  const folded = new Map<string, OutcomeRow>();
  for (const group of groups) {
    const key = group.category ?? '';
    const running = folded.get(key) ?? {
      key,
      label: label(group.category),
      total: 0,
      completed: 0,
      open: 0,
      noShow: 0,
      cancelled: 0,
    };
    folded.set(key, {
      ...running,
      total: running.total + (group.total ?? 0),
      completed: running.completed + (group.completed ?? 0),
      open: running.open + (group.open ?? 0),
      noShow: running.noShow + (group.noShow ?? 0),
      cancelled: running.cancelled + (group.cancelled ?? 0),
    });
  }
  return [...folded.values()].sort((a, b) => b.total - a.total);
}
